import { suratKeluarAPI, userAPI } from '@/services/axios/perki'
import moment from 'moment'

const state = {
  // List state
  suratKeluarList: [],
  listLoading: false,
  listPagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  listFilters: {
    status: null,
    search: '',
  },

  // Detail state
  currentSurat: null,
  detailLoading: false,

  // Form state
  formLoading: false,
  submitLoading: false,
  draftLoading: false,

  // Pengurus options (users in same group)
  pengurusOptions: [],
  pengurusMap: {},
  pengurusLoading: false,

  // Undangan acara options
  undanganAcaraOptions: [],

  // Surat masuk options (for referencing in surat keluar)
  suratMasukOptions: [],
}

const mutations = {
  // List mutations
  SET_SURAT_KELUAR_LIST(state, list) {
    state.suratKeluarList = list
  },
  SET_LIST_LOADING(state, loading) {
    state.listLoading = loading
  },
  SET_LIST_PAGINATION(state, pagination) {
    state.listPagination = { ...state.listPagination, ...pagination }
  },
  SET_LIST_FILTERS(state, filters) {
    state.listFilters = { ...state.listFilters, ...filters }
  },

  // Detail mutations
  SET_CURRENT_SURAT(state, surat) {
    state.currentSurat = surat
  },
  SET_DETAIL_LOADING(state, loading) {
    state.detailLoading = loading
  },

  // Form mutations
  SET_FORM_LOADING(state, loading) {
    state.formLoading = loading
  },
  SET_SUBMIT_LOADING(state, loading) {
    state.submitLoading = loading
  },
  SET_DRAFT_LOADING(state, loading) {
    state.draftLoading = loading
  },

  // Pengurus mutations
  SET_PENGURUS_OPTIONS(state, options) {
    state.pengurusOptions = options
  },
  SET_PENGURUS_MAP(state, map) {
    state.pengurusMap = map
  },
  SET_PENGURUS_LOADING(state, loading) {
    state.pengurusLoading = loading
  },

  // Undangan acara mutations
  SET_UNDANGAN_ACARA_OPTIONS(state, options) {
    state.undanganAcaraOptions = options
  },

  // Surat masuk options mutations
  SET_SURAT_MASUK_OPTIONS(state, options) {
    state.suratMasukOptions = options
  },

  // Clear state
  CLEAR_CURRENT_SURAT(state) {
    state.currentSurat = null
  },
  CLEAR_LIST(state) {
    state.suratKeluarList = []
    state.listPagination = { page: 1, limit: 10, total: 0 }
  },
}

const actions = {
  // Fetch surat keluar list
  async fetchSuratKeluarList({ commit, state }, params = {}) {
    commit('SET_LIST_LOADING', true)
    try {
      const response = await suratKeluarAPI.getAll({
        page: params.page || state.listPagination.page,
        limit: params.limit || state.listPagination.limit,
        status: params.status || state.listFilters.status,
        search: params.search || state.listFilters.search,
      })

      if (response.data.status === 'success') {
        const data = response.data.data
        commit('SET_SURAT_KELUAR_LIST', data.suratKeluar || data || [])
        commit('SET_LIST_PAGINATION', {
          page: data.page || params.page || 1,
          limit: data.limit || params.limit || 10,
          total: data.total || 0,
        })
      }
      return response.data
    } catch (error) {
      console.error('Error fetching surat keluar list:', error)
      throw error
    } finally {
      commit('SET_LIST_LOADING', false)
    }
  },

  // Fetch surat keluar detail
  async fetchSuratDetail({ commit }, id) {
    commit('SET_DETAIL_LOADING', true)
    try {
      const response = await suratKeluarAPI.getById(id)
      if (response.data.status === 'success') {
        commit('SET_CURRENT_SURAT', response.data.data)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching surat detail:', error)
      throw error
    } finally {
      commit('SET_DETAIL_LOADING', false)
    }
  },

  // Fetch pengurus options (users in same group)
  async fetchPengurusOptions({ commit }) {
    commit('SET_PENGURUS_LOADING', true)
    try {
      const response = await userAPI.getUsersInMyGroup({ limit: 1000 })

      if (response.data.status === 'success') {
        const users = response.data.data.users || response.data.data || []

        // Create options for select
        const options = users.map(user => ({
          value: user.id,
          label: `${user.name}${user.jabatan ? ', ' + user.jabatan : ''}${user.npa ? ' (' + user.npa + ')' : ''}`,
        }))

        // Create map for quick lookup
        const map = {}
        users.forEach(user => {
          map[user.id] = user
        })

        commit('SET_PENGURUS_OPTIONS', options)
        commit('SET_PENGURUS_MAP', map)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching pengurus options:', error)
      throw error
    } finally {
      commit('SET_PENGURUS_LOADING', false)
    }
  },

  // Create surat keluar
  async createSuratKeluar({ commit }, formData) {
    const isDraft = formData.isDraft
    commit(isDraft ? 'SET_DRAFT_LOADING' : 'SET_SUBMIT_LOADING', true)
    try {
      const response = await suratKeluarAPI.create(formData)
      return response.data
    } catch (error) {
      console.error('Error creating surat keluar:', error)
      throw error
    } finally {
      commit(isDraft ? 'SET_DRAFT_LOADING' : 'SET_SUBMIT_LOADING', false)
    }
  },

  // Update surat keluar
  async updateSuratKeluar({ commit }, { id, formData }) {
    commit('SET_SUBMIT_LOADING', true)
    try {
      const response = await suratKeluarAPI.update(id, formData)
      if (response.data.status === 'success') {
        commit('SET_CURRENT_SURAT', response.data.data)
      }
      return response.data
    } catch (error) {
      console.error('Error updating surat keluar:', error)
      throw error
    } finally {
      commit('SET_SUBMIT_LOADING', false)
    }
  },

  // Delete surat keluar (draft)
  async deleteSuratKeluar({ commit }, id) {
    commit('SET_SUBMIT_LOADING', true)
    try {
      const response = await suratKeluarAPI.delete(id)
      return response.data
    } catch (error) {
      console.error('Error deleting surat keluar:', error)
      throw error
    } finally {
      commit('SET_SUBMIT_LOADING', false)
    }
  },

  // Cancel surat keluar
  async cancelSuratKeluar({ commit }, payload) {
    commit('SET_SUBMIT_LOADING', true)
    try {
      // Handle both old format (just id) and new format ({ id, alasan })
      let id, alasan
      if (typeof payload === 'string') {
        id = payload
        alasan = null
      } else {
        id = payload.id
        alasan = payload.alasan
      }

      const response = await suratKeluarAPI.cancel(id, { alasan })
      if (response.data.status === 'success') {
        commit('SET_CURRENT_SURAT', response.data.data?.surat || response.data.data)
      }
      return response.data
    } catch (error) {
      console.error('Error canceling surat keluar:', error)
      throw error
    } finally {
      commit('SET_SUBMIT_LOADING', false)
    }
  },

  // Set list filters
  setListFilters({ commit }, filters) {
    commit('SET_LIST_FILTERS', filters)
  },

  // Fetch surat masuk options for dropdown
  async fetchSuratMasukOptions({ commit }) {
    try {
      const response = await suratKeluarAPI.getSuratMasukOptions()
      if (response.data.status === 'success') {
        const options = response.data.data || []
        // Transform to select format
        const selectOptions = options.map(item => ({
          value: item.id,
          label: item.label,
          nomorSurat: item.nomorSurat,
          pengirim: item.pengirim,
          perihal: item.perihal,
        }))
        commit('SET_SURAT_MASUK_OPTIONS', selectOptions)
      }
      return response.data
    } catch (error) {
      console.error('Error fetching surat masuk options:', error)
      throw error
    }
  },

  // Clear current surat
  clearCurrentSurat({ commit }) {
    commit('CLEAR_CURRENT_SURAT')
  },
}

const getters = {
  // Get user from pengurus map
  getUserById: (state) => (userId) => {
    return state.pengurusMap[userId] || null
  },

  // Get jabatan from userId
  getJabatanByUserId: (state) => (userId) => {
    if (!userId) return '-'
    const user = state.pengurusMap[userId]
    return user?.jabatan || '-'
  },

  // Get NPA from userId
  getNpaByUserId: (state) => (userId) => {
    if (!userId) return '-'
    const user = state.pengurusMap[userId]
    return user?.npa || '-'
  },

  // Format role for display
  formatRole: () => (role) => {
    if (!role) return '-'
    const roleMap = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'sekretaris': 'Staf Sekretariat',
      'ketua': 'Ketua',
      'bendahara': 'Bendahara',
      'pengurus': 'Pengurus',
      'pekerja_harian': 'Pekerja Harian',
    }
    return roleMap[role] || role
  },

  // Get status color (aligned with list implementation)
  getStatusColor: () => (status) => {
    switch (status) {
      case 'Draft': return 'warning'
      case 'Terkirim': return 'cyan'
      case 'Dibatalkan': return 'default'
      case 'Verifikasi': return 'red'
      case 'Penandatanganan': return 'cyan'
      case 'Revisi': return 'orange'
      case 'Revisi Penandatangan': return 'orange'
      case 'Belum Dikirim': return 'orange'
      default: return 'default'
    }
  },

  // Get status style (custom background and text colors - aligned with list implementation)
  getStatusStyle: () => (status) => {
    const s = String(status || '')
    const palette = {
      'Draft': { base: '#FAAE13', bg: '#FFFBE6' },
      'Terkirim': { base: '#51A622', bg: '#F6FFED' },
      'Dibatalkan': { base: '#D9D9D9', bg: '#FAFAFA' },
      'Verifikasi': { base: '#0957D9', bg: '#E5F4FF' },
      'Penandatanganan': { base: '#0B979D', bg: '#E5FFFB' },
      'Revisi': { base: '#541CAB', bg: '#FAF1FF' },
      'Revisi Penandatangan': { base: '#C41E7F', bg: '#FFF0F6' },
      'Belum Dikirim': { base: '#D57117', bg: '#FFF7E5' },
    }
    const c = palette[s] || { base: '#d9d9d9', bg: '#f5f5f5' }
    return {
      color: c.base,
      backgroundColor: c.bg,
      border: `1px solid ${c.base}`,
    }
  },

  // Format verifikator flow for display
  formatVerifikatorFlow: () => (verifikator) => {
    if (!verifikator || verifikator.length === 0) return '-'

    return verifikator.map((v) => {
      const userName = v.user?.name || 'Unknown'
      let statusText = ''

      if (v.status === 'Disetujui') {
        statusText = '(Setuju)'
      } else if (v.status === 'Sedang Verifikasi') {
        statusText = '(Sedang Verifikasi)'
      } else if (v.status === 'Menunggu') {
        statusText = '(Menunggu)'
      }

      return `${userName} ${statusText}`
    }).join(' → ')
  },

  // Format alur verifikasi
  formatAlurVerifikasi: () => (verifikator) => {
    if (!verifikator || verifikator.length === 0) return '-'

    const parts = verifikator.map((v, index) => {
      const userName = v.user?.name || 'Unknown'
      const jabatan = v.user?.jabatan || ''
      return `${userName}${jabatan ? ', ' + jabatan : ''} (Verifikator ${index + 1})`
    })

    return parts.join(' → ') + ' → Selesai Verifikasi & Masuk Proses Penandatanganan'
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
